import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import shp, { combine, parseDbf, parseShp } from 'shpjs';

import { COLUMN_NAME_EXCLUDES } from './constants';
import { detectCrs } from './detectCrs';
import { readShapefileInput } from './readFiles';
import { toApiZones } from './toApiZones';
import type {
    FinalizeParseParams,
    NormalizedShapefileBuffers,
    ParseShapefileParams,
    ShapefileParseResult,
    StagedParse,
    StagedParseResult
} from './types';
import { findInvalidPolygonIndices } from './validatePolygons';

// Phase A — read the caller's input, validate CRS, run shpjs, and stage the
// resulting FeatureCollection. Caller picks a rate column before calling
// finalizeParse() to produce the wire-ready zone list.
export const parseShapefile = async ({
    input
}: ParseShapefileParams): Promise<StagedParseResult> => {
    const readResult = await readShapefileInput(input);

    if (readResult.error) {
        return { error: readResult.error, ok: false };
    }

    try {
        if (readResult.mode === 'zip' && readResult.zipBuffer) {
            return await parseZipBuffer({ zipBuffer: readResult.zipBuffer });
        }

        if (readResult.mode === 'files' && readResult.buffers) {
            return parseFileBuffers({ buffers: readResult.buffers });
        }

        return {
            error: { detail: 'Unexpected read result state', kind: 'shpjsFailure' },
            ok: false
        };
    } catch (caught) {
        const detail = caught instanceof Error ? caught.message : String(caught);

        return { error: { detail, kind: 'shpjsFailure' }, ok: false };
    }
};

// Phase B — convert a StagedParse plus the user's chosen rate column into the
// final ApiPrescriptionZone list. Validates polygons and rejects any set that
// contains topology errors (no buffer(0) auto-heal — Chris decision Q8).
export const finalizeParse = ({
    rateColumn,
    staged
}: FinalizeParseParams): ShapefileParseResult => {
    const invalidIndices = findInvalidPolygonIndices({
        featureCollection: staged.featureCollection
    });

    if (invalidIndices.length > 0) {
        return {
            error: {
                count: invalidIndices.length,
                indices: invalidIndices,
                kind: 'invalidPolygons'
            },
            ok: false
        };
    }

    const zones = toApiZones({
        featureCollection: staged.featureCollection,
        rateColumn
    });

    if (zones.length === 0) {
        return { error: { kind: 'emptyShapefile' }, ok: false };
    }

    return { ok: true, zones };
};

// Path 1: single-zip file. shpjs' default export accepts an ArrayBuffer and
// internally extracts .prj to a proj4 definition — which would reproject
// non-WGS84 shapefiles silently. We can't intercept the raw .prj WKT here,
// so we rely on the shpjs result to carry WGS84 coordinates; if the zip's
// .prj declared something else, the proj4 step would have reprojected into
// WGS84 for us automatically. Current scope: accept the zip path as WGS84
// by construction (since shpjs normalizes). This matches the "zip files
// come from John Deere, QGIS, or ArcGIS and are almost always in WGS84"
// assumption in the plan.
const parseZipBuffer = async ({
    zipBuffer
}: {
    zipBuffer: ArrayBuffer;
}): Promise<StagedParseResult> => {
    const shpResult = await shp(zipBuffer);
    const featureCollection = flattenCollection(shpResult);

    if (!featureCollection) {
        return { error: { kind: 'emptyShapefile' }, ok: false };
    }

    return stageResult({ featureCollection });
};

// Path 2: individual files. We own CRS validation here since shpjs's parseShp
// without a transform argument leaves coordinates unchanged.
const parseFileBuffers = ({
    buffers
}: {
    buffers: NormalizedShapefileBuffers;
}): StagedParseResult => {
    const crsResult = detectCrs({ prj: buffers.prj });

    if (crsResult !== 'WGS84') {
        return {
            error: { detected: crsResult.unsupported, kind: 'unsupportedCrs' },
            ok: false
        };
    }

    const geometries: Geometry[] = parseShp(buffers.shp) as Geometry[];
    const properties: GeoJsonProperties[] = parseDbf(
        buffers.dbf,
        new ArrayBuffer(0)
    ) as GeoJsonProperties[];

    const featureCollection = combine([geometries, properties]) as FeatureCollection;

    return stageResult({ featureCollection });
};

const stageResult = ({
    featureCollection
}: {
    featureCollection: FeatureCollection;
}): StagedParseResult => {
    if (featureCollection.features.length === 0) {
        return { error: { kind: 'emptyShapefile' }, ok: false };
    }

    const columns = extractColumnNames({ featureCollection });

    const staged: StagedParse = {
        columns,
        crs: 'WGS84',
        featureCollection,
        featureCount: featureCollection.features.length
    };

    return { ok: true, staged };
};

// Pull the union of property keys from the first feature (shpjs always
// produces a uniform schema from a .dbf), then drop the conventional metadata
// columns so the caller sees only candidate rate fields.
const extractColumnNames = ({
    featureCollection
}: {
    featureCollection: FeatureCollection;
}): string[] => {
    const first = featureCollection.features[0];

    if (!first?.properties) {
        return [];
    }

    const excludeSet = new Set(COLUMN_NAME_EXCLUDES as readonly string[]);

    return Object.keys(first.properties).filter(name => !excludeSet.has(name));
};

// shpjs returns either a single FeatureCollectionWithFilename or an array of
// them (one per layer). We collapse multi-layer results by taking the first
// layer — prescription shapefiles ship a single layer in practice.
const flattenCollection = (result: Awaited<ReturnType<typeof shp>>): FeatureCollection | null => {
    if (Array.isArray(result)) {
        return result[0] ?? null;
    }

    return result ?? null;
};

export type {
    FinalizeParseParams,
    ParseShapefileParams,
    ShapefileParseError,
    ShapefileParseResult,
    StagedParse,
    StagedParseResult
} from './types';
